import { kyInstance, kyPublicInstance, getAcceptLanguage, getApiKeyHeader } from "@/core/api/client";
import { useSessionStore } from "@/core/stores/session.store";
import { CONSTANTS } from "@/core/utils/func/constants";
import { getDeviceHeaders } from "@/core/utils/func/deviceHeaders";
import { notifSilenceError } from "@/core/utils/func/notifApp";
import type { Options } from "ky";

const API_BASE_URL = CONSTANTS.BASE_URL;

/**
 * Préfixe proxy vite de dev (`/app` par défaut) — miroir de $WA où BASE_URL
 * vaut le préfixe proxy quand le BFF est actif. Utilisé quand
 * VITE_API_BASE_URL est vide (dev) : les requêtes restent relatives et
 * passent par le proxy vite (`server.proxy`).
 */
const PROXY_BASE_URL = (() => {
  const raw = import.meta.env.VITE_PROXY_BASE_URL || "/app";
  return raw.startsWith("/") ? raw : `/${raw}`;
})();

export const buildApiUrl = (url: string): string => {
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = (API_BASE_URL || PROXY_BASE_URL).replace(/\/$/, "");
  return `${base}/${url.replace(/^\//, "")}`;
};

const accessToken = () => useSessionStore.getState().accessToken ?? "";

/** GET authentifié via fetch natif (token explicite requis). */
export const fetchDataGet = async <T = unknown>({
  url,
  queryParams = {},
  token = useSessionStore.getState().accessToken ?? undefined,
  logoutOn401 = true,
}: {
  url: string;
  queryParams?: Record<string, string>;
  token?: string;
  /**
   * Si false, un 401 lève une erreur sans déconnecter (ex. feature pas encore
   * autorisée côté API). Défaut true.
   */
  logoutOn401?: boolean;
}): Promise<T> => {
  if (!token) {
    throw new Error("Authorization token is missing. Please log in.");
  }

  const queryString = new URLSearchParams(queryParams).toString();
  const fullUrl = `${buildApiUrl(url)}${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(fullUrl, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "Accept-Language": getAcceptLanguage(),
      ...getApiKeyHeader(),
      ...getDeviceHeaders(),
    },
    method: "GET",
  });

  if (response?.status === 401 && accessToken()) {
    notifSilenceError(`Error fetching data: ${response.statusText}`, 15000);
    if (logoutOn401) {
      useSessionStore.getState().clearSession();
      // Redirection optionnelle une fois le routeur en place :
      // window.location.assign("/login");
    }
    throw new Error("401 Unauthorized");
  }

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new Error(
        `Error fetching data: ${response.status} ${response.statusText}`
      );
    }
    throw new Error("Invalid JSON response");
  }

  // Évite de traiter un corps d'erreur HTTP comme une réponse valide.
  if (!response.ok) {
    const payload = data as { message?: unknown } | null;
    const rawMsg = payload?.message;
    const message =
      typeof rawMsg === "string" && rawMsg.trim()
        ? rawMsg
        : `Error fetching data: ${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  return data as T;
};

/** GET via kyInstance — fallback texte si le corps n'est pas du JSON. */
export const fetchData = async <T = unknown>(url: string): Promise<T> => {
  // ky lève SyntaxError sur les réponses non-JSON (PEM, texte brut) ;
  // on reproduit le fallback texte d'axios (defaults.transformResponse).
  const response = await kyInstance.get(buildApiUrl(url));
  const text = await response.text();
  if (!text) return null as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as T;
  }
};

/**
 * Mutation AUTHENTIFIÉE via kyInstance (Bearer injecté, logout sur 401).
 *
 * Règle : endpoints authentifiés → `mutateData`/`kyInstance` ;
 * endpoints publics sans session (login, refresh, register) →
 * `mutatePublicData`/`kyPublicInstance` (sinon un Bearer périmé est envoyé et
 * un 401 de login viderait la session).
 */
export const mutateData = async ({
  url,
  reqBody,
  method,
  idempotencyKey,
}: {
  url: string;
  reqBody?: Record<string, unknown>;
  method: "PUT" | "PATCH" | "DELETE" | "POST";
  /**
   * En-tête `Idempotency-Key` (guide §4.7) — obligatoire sur les endpoints
   * qui engagent de l'argent. Générer via `createIdempotencyScope()`
   * (`@/core/api/idempotency`) : même clé au retry, nouvelle clé par
   * tentative métier.
   */
  idempotencyKey?: string;
}) => {
  const opts: Options = {
    method: method.toLowerCase(),
    headers: {
      "Accept-Language": getAcceptLanguage(),
      ...getDeviceHeaders(),
      ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
    },
  };
  if (reqBody !== undefined) opts.json = reqBody;

  const response = await kyInstance(buildApiUrl(url), opts);

  let data: any = {};
  try {
    const text = await response.text();
    if (text.trim()) data = JSON.parse(text);
  } catch {
    /* corps vide ou non-JSON */
  }

  const statusServer = response.status;
  const statusTextServer = response.statusText;
  const statusTextHeader = response.headers;

  return {
    ...data,
    statusServer,
    statusTextServer,
    statusTextHeader,
    statusTextConfig: undefined,
    statusTextRequest: undefined,
  };
};

/**
 * Mutation PUBLIQUE (sans auth) via kyPublicInstance — mêmes options et même
 * enveloppe `statusServer`/`statusTextServer` que `mutateData`.
 *
 * Règle : endpoints publics sans session (login, refresh, register) →
 * `mutatePublicData`/`kyPublicInstance` ; endpoints authentifiés →
 * `mutateData`/`kyInstance`. Un endpoint public appelé via `mutateData`
 * enverrait un Bearer périmé et un 401 viderait la session (hook beforeError).
 */
export const mutatePublicData = async ({
  url,
  reqBody,
  method,
}: {
  url: string;
  reqBody?: Record<string, unknown>;
  method: "PUT" | "PATCH" | "DELETE" | "POST";
}) => {
  const opts: Options = {
    method: method.toLowerCase(),
    headers: {
      "Accept-Language": getAcceptLanguage(),
      ...getDeviceHeaders(),
    },
  };
  if (reqBody !== undefined) opts.json = reqBody;

  const response = await kyPublicInstance(buildApiUrl(url), opts);

  let data: any = {};
  try {
    const text = await response.text();
    if (text.trim()) data = JSON.parse(text);
  } catch {
    /* corps vide ou non-JSON */
  }

  const statusServer = response.status;
  const statusTextServer = response.statusText;
  const statusTextHeader = response.headers;

  return {
    ...data,
    statusServer,
    statusTextServer,
    statusTextHeader,
    statusTextConfig: undefined,
    statusTextRequest: undefined,
  };
};

/**
 * Envoi multipart/form-data via kyInstance.
 *
 * Ne PAS fixer manuellement `Content-Type` : ky/fetch posent automatiquement
 * `multipart/form-data` avec le bon `boundary` quand le body est un `FormData`.
 * La gestion des erreurs (beforeError → log + logout 401) s'applique via
 * kyInstance ; les autres statuts d'erreur lèvent une `HTTPError` que
 * l'appelant doit catcher.
 */
export const mutateFormData = async <T = unknown>({
  url,
  formData,
  method = "POST",
}: {
  url: string;
  formData: FormData;
  method?: "POST" | "PUT" | "PATCH";
}): Promise<T> => {
  const opts: Options = {
    method: method.toLowerCase(),
    body: formData,
    headers: {
      "Accept-Language": getAcceptLanguage(),
      ...getDeviceHeaders(),
    },
  };

  const response = await kyInstance(buildApiUrl(url), opts);
  const text = await response.text();
  try {
    return (text.trim() ? JSON.parse(text) : {}) as T;
  } catch {
    return {} as T;
  }
};

/**
 * Mutation dont la réponse est un binaire — retourne une objectURL.
 * N'envoie volontairement pas d'Authorization : endpoints blob publics
 * (miroir du comportement source web-app).
 */
export const mutateBlobData = async ({
  url,
  reqBody,
  method,
}: {
  url: string;
  reqBody: Record<string, unknown>;
  method: "PUT" | "PATCH" | "DELETE" | "POST";
}) => {
  const response = await fetch(buildApiUrl(url), {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Accept-Language": getAcceptLanguage(),
      ...getApiKeyHeader(),
      ...getDeviceHeaders(),
    },
    method,
    body: JSON.stringify(reqBody),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

/** GET authentifié retournant un Blob (exports CSV §7.3, PDF…). */
export const fetchDataGetBlob = async ({
  url,
  queryParams = {},
  acceptHeader = "application/json",
  token = useSessionStore.getState().accessToken ?? undefined,
}: {
  url: string;
  queryParams?: Record<string, string>;
  acceptHeader?: string;
  token?: string;
}) => {
  if (!token) {
    throw new Error("Authorization token is missing. Please log in.");
  }

  const queryString = new URLSearchParams(queryParams).toString();
  const fullUrl = `${buildApiUrl(url)}${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(fullUrl, {
    headers: {
      Accept: acceptHeader,
      Authorization: `Bearer ${token}`,
      "Accept-Language": getAcceptLanguage(),
      ...getApiKeyHeader(),
      ...getDeviceHeaders(),
    },
    method: "GET",
  });

  if (response?.status === 401 && accessToken()) {
    notifSilenceError(`Error fetching data: ${response.statusText}`, 15000);
    useSessionStore.getState().clearSession();
    // Redirection optionnelle une fois le routeur en place :
    // window.location.assign("/login");
    throw new Error("401 Unauthorized");
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      text || `Error fetching data: ${response.status} ${response.statusText}`
    );
  }

  return response.blob();
};

/** Alias CSV / binaire (guide §7.3 `export.csv`). */
export const fetchDataBlob = fetchDataGetBlob;

/** Blob + nom de fichier porté par l'en-tête `Content-Disposition`. */
export interface BlobFile {
  blob: Blob;
  /** Nom extrait de `Content-Disposition` (null si l'en-tête est absente). */
  filename: string | null;
}

/**
 * Extrait le `filename` d'un `Content-Disposition` (`filename*=UTF-8''…`
 * prioritaire, sinon `filename="…"`).
 */
const filenameFromContentDisposition = (header: string | null): string | null => {
  if (!header) return null;
  const star = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      return star[1].trim();
    }
  }
  const plain = header.match(/filename\s*=\s*"?([^";]+)"?/i);
  return plain?.[1]?.trim() ?? null;
};

/**
 * GET authentifié retournant le Blob **et** le nom de fichier de
 * `Content-Disposition` (téléchargements PDF — ex. `Statements/requests/{id}/download`,
 * guide v3.1). Même gestion d'erreurs que `fetchDataGetBlob`.
 */
export const fetchDataGetBlobFile = async ({
  url,
  queryParams = {},
  acceptHeader = "application/json",
  token = useSessionStore.getState().accessToken ?? undefined,
}: {
  url: string;
  queryParams?: Record<string, string>;
  acceptHeader?: string;
  token?: string;
}): Promise<BlobFile> => {
  if (!token) {
    throw new Error("Authorization token is missing. Please log in.");
  }

  const queryString = new URLSearchParams(queryParams).toString();
  const fullUrl = `${buildApiUrl(url)}${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(fullUrl, {
    headers: {
      Accept: acceptHeader,
      Authorization: `Bearer ${token}`,
      "Accept-Language": getAcceptLanguage(),
      ...getApiKeyHeader(),
      ...getDeviceHeaders(),
    },
    method: "GET",
  });

  if (response?.status === 401 && accessToken()) {
    notifSilenceError(`Error fetching data: ${response.statusText}`, 15000);
    useSessionStore.getState().clearSession();
    throw new Error("401 Unauthorized");
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      text || `Error fetching data: ${response.status} ${response.statusText}`
    );
  }

  return {
    blob: await response.blob(),
    filename: filenameFromContentDisposition(
      response.headers.get("Content-Disposition")
    ),
  };
};
